// @flow
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { translate } from 'react-i18next';

import React, { Component } from 'react';
import RaisedButton from 'material-ui/RaisedButton';
import { Dialog } from 'common/components/ui/Dialog';
import Button from 'common/components/ui/Button';
import styled, { withTheme } from 'styled-components';

import { getToken } from 'statsradio/modules/authentication/session/selectors/sessionSelectors';

import LoadingSpinner from 'common/components/LoadingSpinner';
import { openSnackbar } from 'common/modules/ui/actions/uiActions';
import { isDevFlagEnabled } from 'common/components/DevFlag';
import Radio from 'common/domain/radio';

import DialogFooter from '../../../components/DialogFooter';
import RadioCustomer from '../types/RadioCustomer';
import DeleteButton from '../../../components/DeleteButton';
import EditCustomerForm from './EditCustomerForm';
import CustomersAPI from '../services/CustomersAPI';
import { getRadio } from '../../authentication/session/reducers/sessionReducer';
import { deleteCustomer, inviteCustomer } from '../actions/customersActions';
import type { CustomerInvitation } from '../types/RadioCustomer';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.metrics.largeMargin}px;
`;

const Text = styled.h4`
  padding: 0;
  margin: 0;
  margin-bottom: ${({ theme }) => theme.metrics.smallMargin}px;
`;

const SubText = styled.h5`
  padding: 0;
  margin: 0;
  margin-bottom: ${({ theme }) => theme.metrics.smallMargin}px;
`;

const Information = styled.div`
  color: ${({ theme }) => theme.colors.primaryText};
`;

type Props = {
  visible: boolean,
  customer: RadioCustomer,
  customerDeletion: Object,
  customerInvitation: Object,
  t: any,
  theme: any,
  radio: Radio,
  token: string,

  onEditSuccess: (RadioCustomer[]) => void,
  onClose: () => void,
  actions: {
    inviteCustomer: (customer: CustomerInvitation) => Promise<*>,
    deleteCustomer: (customer: RadioCustomer) => Promise<*>,
    openSnackbar: (type: string, message: string) => Promise<*>,
  },
};

type State = {
  canSave: boolean,
  modifiedCustomer: RadioCustomer,
}

class EditCustomerDialog extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      canSave: false,
      modifiedCustomer: props.customer,
    };
  }

  onResendInvitation = (): void => {
    const {
      t, customer, customerInvitation, actions,
    } = this.props;
    if (customerInvitation.sending) {
      return;
    }
    actions.openSnackbar('message', t('sendingInvitation'));

    const invitation = {
      advertiserName: customer.getAdvertiserName(),
      emailAddress: customer.getContactEmail(),
      fullname: customer.getContactFullName(),
      giveAccessToFollo: !isDevFlagEnabled('FOLLO_WITHOUT_FOLLO'),
    };

    actions.inviteCustomer(invitation)
      .then(() => {
        if (customerInvitation.error) {
          actions.openSnackbar('error', t('couldNotSendInvitationMessage'));
        } else {
          actions.openSnackbar('message', t('invitationSendSuccess'));
        }
      });
  };

  editCustomer = async () => {
    const { t } = this.props;
    const callSign = this.props.radio.callSign;
    const token = this.props.token;
    try {
      const radioCustomers = await CustomersAPI.editCustomer(callSign, token, this.state.modifiedCustomer);
      this.props.onEditSuccess(radioCustomers);
      this.props.actions.openSnackbar('message', t('editedCustomerSuccess'));
    } catch (error) {
      this.props.actions.openSnackbar('error', t('couldNotEditCustomer'));
      this.props.onClose();
    }
  };

  // TODO STATS-1607 Remove after releasing FSF
  onDeleteCustomerConfirmed = (): void => {
    const {
      t, customerDeletion, actions, customer, onClose,
    } = this.props;
    if (!customerDeletion.deleting) {
      actions.deleteCustomer(customer).then(() => {
        if (customerDeletion.error) {
          actions.openSnackbar('error', t('couldNotDeleteCustomer'));
        }

        onClose();
      });
    }
  };

  onInvalidCustomerChange = (): void => {
    this.setState({ canSave: false });
  };

  onValidCustomerChange = (customer: RadioCustomer): void => {
    this.setState({ canSave: true, modifiedCustomer: customer });
  };

  render() {
    const { t, theme } = this.props;

    const actions = [
      <Button key="cancel" variant="outlined" color="primary" onClick={this.props.onClose}>{t('cancel')}</Button>,
      <Button key="save" variant="contained" color="primary" onClick={this.editCustomer} disabled={!this.state.canSave}>{t('save')}</Button>,
    ];

    return (
      <Dialog
        title={t('editCustomer')}
        open={this.props.visible}
        actions={actions}
        disableScroll
        onClose={this.props.onClose}
      >
        {!isDevFlagEnabled('EDIT_RADIOCUSTOMER') && (!this.props.customerDeletion.deleting) && this.props.customer &&
          <div>
            <Wrapper>
              <div className="row">
                <div className="col-lg-12 col-xs-12">
                  <Text>{t('company')}</Text>
                  <Information>{this.props.customer.getAdvertiserName()}</Information>
                </div>
              </div>
              <div className="row" style={{ marginTop: theme.metrics.margin }}>
                <div className="col-lg-12 col-xs-12">
                  <Text>{t('contact')}</Text>
                </div>
              </div>
              <div className="row" style={{ marginTop: theme.metrics.smallMargin }}>
                <div className="col-lg-12 col-xs-12">
                  <SubText>{t('email')}</SubText>
                  <Information>{this.props.customer.getContactEmail()}</Information>
                </div>
              </div>
              <div className="row" style={{ marginTop: theme.metrics.smallMargin }}>
                <div className="col-lg-12 col-xs-12">
                  <SubText>{t('fullname')}</SubText>
                  <Information>{this.props.customer.getContactFullName()}</Information>
                </div>
              </div>
              <div className="row" style={{ marginTop: theme.metrics.smallMargin }}>
                <div className="col-lg-12 col-xs-12">
                  <SubText>{t('accountStatus')}</SubText>
                  <Information>{this.props.customer.isRegistered ? t('active') : t('invited')}</Information>
                </div>
              </div>
              <div className="row" style={{ marginTop: 2 * theme.metrics.largeMargin }}>
                <div className="col-lg-12 col-xs-12" style={{ display: 'flex', flexDirection: 'column' }}>
                  {!this.props.customer.hasAcceptedInvitation() ?
                    <RaisedButton
                      style={{ width: '40%', marginBottom: theme.metrics.smallMargin }}
                      onClick={this.onResendInvitation}
                      label={t('resendInvitation')}
                    /> : <div />
                  }
                  <DeleteButton
                    label={t('deleteCustomer')}
                    confirmationQuestionText={t('deleteCustomerQuestion')}
                    confirmText={t('yes')}
                    cancelText={t('no')}
                    onConfirmation={this.onDeleteCustomerConfirmed}
                  />
                </div>
              </div>
            </Wrapper>
            <DialogFooter>
              <RaisedButton onClick={this.props.onClose} label={t('close')} />
            </DialogFooter>
          </div>
        }
        {!isDevFlagEnabled('EDIT_RADIOCUSTOMER') && (this.props.customerDeletion.deleting) && <Wrapper><LoadingSpinner size="medium" /></Wrapper>}
        {isDevFlagEnabled('EDIT_RADIOCUSTOMER') &&
          <EditCustomerForm customer={this.props.customer} onInvalidCustomerChange={this.onInvalidCustomerChange} onValidCustomerChange={this.onValidCustomerChange} />
        }
      </Dialog>
    );
  }
}

function mapStateToProps(state) {
  return {
    radio: getRadio(state),
    token: getToken(state),

    // TODO STATS-1607 Remove after releasing FSF
    customerInvitation: {
      sending: state.customers.customerInvitation.sending,
      error: state.customers.customerInvitation.error,
    },
    customerDeletion: {
      deleting: state.customers.customerDeletion.deleting,
      error: state.customers.customerDeletion.error,
    },
  };
}

function mapDispatchToProps(dispatch: *): Object {
  return {
    actions: bindActionCreators({
      openSnackbar,

      // TODO STATS-1607 Remove after releasing FSF
      inviteCustomer,
      deleteCustomer,
    }, dispatch),
  };
}

export default translate('customers')(withTheme(connect(mapStateToProps, mapDispatchToProps)(EditCustomerDialog)));



// WEBPACK FOOTER //
// ./src/statsradio/modules/customers/components/EditCustomerDialog.js